import { Link } from "@tanstack/react-router";
import { BUSINESS, BLOG_POSTS } from "./constants";
import { OWNER_ID, ROOFING_CONTRACTOR_ID, SITE_URL, canonical } from "./site-config";
import { breadcrumbListJsonLd } from "./Breadcrumbs";
import { PageHero } from "./PageShell";
import { Breadcrumbs } from "./Breadcrumbs";
import { CTASection } from "./CTASection";

type Post = (typeof BLOG_POSTS)[number];

export function blogPostHead(post: Post) {
  return {
    meta: [
      { title: `${post.seoTitle} | Destination Roofing` },
      { name: "description", content: post.description },
      { property: "og:title", content: post.seoTitle },
      { property: "og:description", content: post.description },
      { property: "og:url", content: canonical(post.slug) },
      { property: "og:type", content: "article" },
      { property: "article:published_time", content: post.datePublished },
      { property: "article:modified_time", content: post.dateModified },
      { property: "article:author", content: "Deston Judy" },
    ],
    links: [{ rel: "canonical", href: canonical(post.slug) }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([
          { label: "Blog", href: "/blog" },
          { label: post.title, href: post.slug },
        ]),
        {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "@id": canonical(post.slug) + "#article",
          mainEntityOfPage: canonical(post.slug),
          headline: post.seoTitle,
          description: post.description,
          url: canonical(post.slug),
          datePublished: post.datePublished,
          dateModified: post.dateModified,
          author: {
            "@type": "Person",
            "@id": OWNER_ID,
            name: "Deston Judy",
            url: SITE_URL + "/about#deston-judy",
            jobTitle: "Owner, Destination Roofing Inc.",
          },
          publisher: { "@id": ROOFING_CONTRACTOR_ID },
          articleSection: post.articleSection,
          inLanguage: "en-US",
        },
      ]),
    }],
  };
}

export function BlogPostShell({
  post,
  toc,
  children,
}: {
  post: Post;
  toc: Array<{ id: string; label: string }>;
  children: React.ReactNode;
}) {
  const related = BLOG_POSTS.filter(p => p.slug !== post.slug);
  return (
    <>
      <Breadcrumbs items={[{ label: "Blog", href: "/blog" }, { label: post.title, href: post.slug }]} />
      <PageHero
        eyebrow="Roofing Advice"
        title={post.title}
        subtitle={`By ${BUSINESS.owner}, Owner of Destination Roofing Inc. · Published ${new Date(post.datePublished).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`}
      />
      <article className="mx-auto max-w-4xl px-4 py-12 md:py-16">
        <div className="rounded-xl border border-border bg-white p-4 md:p-6 mb-8">
          <div className="text-sm font-bold uppercase tracking-wider text-cta">On this page</div>
          <ol className="mt-2 grid gap-1 sm:grid-cols-2 text-sm">
            {toc.map(item => (
              <li key={item.id}>
                <a href={`#${item.id}`} className="text-brand hover:underline">{item.label}</a>
              </li>
            ))}
          </ol>
        </div>

        <div className="prose prose-slate max-w-none prose-h2:mt-10 prose-h2:font-display prose-h3:font-display prose-a:text-brand">
          {children}
        </div>

        <div className="mt-10 rounded-xl border border-border bg-white p-5 md:p-6" id="author">
          <div className="text-xs font-bold uppercase tracking-wider text-cta">About the author</div>
          <div className="mt-2 font-display font-bold text-lg">
            <Link to="/about" className="hover:underline">{BUSINESS.owner}</Link>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {BUSINESS.owner} is the owner of Destination Roofing Inc., a family-owned roofing contractor serving Citrus County, FL. Florida native, Florida-licensed roofer ({BUSINESS.license}), and {BUSINESS.atlasCredential}. <Link to="/about" className="text-brand underline">Read more about the company →</Link>
          </p>
        </div>

        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display font-bold text-2xl">Related articles</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {related.map(r => (
                <Link key={r.slug} to={r.slug} className="rounded-xl border border-border bg-white p-5 hover:border-brand transition-colors">
                  <div className="font-display font-bold">{r.title}</div>
                  <div className="text-sm text-muted-foreground mt-2">{r.excerpt}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
      <CTASection />
    </>
  );
}
